import type { ChangeEvent } from "react";
import { trackEvent } from "src/services/segment";
import { trackExploreJourneyEvent } from "src/services/exploreOrganisationAnalytics";
import { EVENT_TRACKING } from "src/consts/eventTracking";

export type TrackSearchFilterArgs = {
  emitLegacy?: boolean;
  legacyProps?: { setting: string; value: unknown };
  journeyProps?: Record<string, unknown>;
};

export const trackSearchFilter = ({
  emitLegacy = true,
  legacyProps,
  journeyProps = {},
}: TrackSearchFilterArgs): void => {
  // LEGACY until 2026-11-17 — keep prior check-only volume for legacy dashboards.
  if (emitLegacy && legacyProps) {
    trackEvent(EVENT_TRACKING.SEARCH_FILTERED, legacyProps);
  }
  trackExploreJourneyEvent(EVENT_TRACKING.SEARCH_FILTER_COMPLETED, {
    lifecycle_stage: "completed",
    entry_point: "filter_sidebar",
    operation_intent: "search",
    ...journeyProps,
  });
};

/**
 * Builds a checkbox change handler for the "toggle a named filter on/off"
 * shape shared by the people, position-field, and team-type sub-filters:
 * checked adds the filter and tracks it; unchecked removes it and tracks it
 * without the legacy event. `legacyValue` lets team-type preserve its
 * existing (name-independent) legacy tracking value.
 */
export const createToggleFilterHandler =
  (
    setting: string,
    add: (name: string) => void,
    remove: (name: string) => void,
    legacyValue: (name: string) => unknown = (name) => name
  ) =>
  (e: ChangeEvent<HTMLInputElement>): void => {
    const { checked, name } = e.target;
    if (checked) {
      add(name);
      trackSearchFilter({
        legacyProps: { setting, value: legacyValue(name) },
        journeyProps: {
          filter_setting: setting,
          filter_value: name,
          filter_checked: true,
        },
      });
    } else {
      remove(name);
      trackSearchFilter({
        emitLegacy: false,
        journeyProps: {
          filter_setting: setting,
          filter_value: name,
          filter_checked: false,
        },
      });
    }
  };
